'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { TODOS, VISIBLES } from '@/lib/idiomas';

// El menú muestra los visibles; para reconocer el idioma del path vale cualquiera
// (lib/idiomas.js).
const LOCALES = VISIBLES;

export default function LanguageSelect() {
  const router = useRouter();
  const pathname = usePathname();           // p.ej. '/es/otra-ruta' o '/otra-ruta' (sin locale después de rewrite)
  const searchParams = useSearchParams();   // p.ej. ?foo=bar
  const { t, i18n } = useTranslation('checkout');

  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  // Extraer locale del pathname, o usar el de i18n como fallback
  // Esto es importante porque después de un rewrite, el pathname del browser
  // puede no tener locale, pero i18n sí tiene el idioma correcto
  const pathSegment = pathname.split('/')[1] || '';
  const currentLocale = TODOS.includes(pathSegment) 
    ? pathSegment 
    : (i18n.language || LOCALES[0]);

  const toggle = () => setIsOpen(!isOpen);

  const setLocaleCookie = (loc) => {
    // persistimos 30 días; SameSite=Lax; Secure si https
    Cookies.set('ln', loc, {
      expires: 30,
      sameSite: 'Lax',
      secure: typeof window !== 'undefined' ? window.location.protocol === 'https:' : true,
      path: '/',
    });
  };

  const changeLocale = (newLocale) => {
    setIsOpen(false);
    if (newLocale === currentLocale) return;

    // 1) persistir preferencia
    setLocaleCookie(newLocale);

    // 2) reconstruir la URL con el nuevo locale
    // NOTA: NO llamamos i18n.changeLanguage() aquí porque el TranslationsProvider
    // solo tiene recursos del locale actual. Cambiar el idioma sin recursos
    // causa fallback a "es". La navegación cargará los recursos correctos.
    const segments = pathname.split('/');
    const firstSegment = segments[1] || '';
    
    // Si el primer segmento es un locale válido, lo reemplazamos
    // Si no (después de un rewrite), insertamos el locale al principio
    if (TODOS.includes(firstSegment)) {
      segments[1] = newLocale;
    } else {
      segments.splice(1, 0, newLocale);
    }
    
    let newPath = segments.join('/');
    const qs = searchParams.toString();
    if (qs) newPath += `?${qs}`;

    // 3) navegar
    router.push(newPath);
  };

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  return (
    <li className="languageSelect" ref={ref}>
      <a
        href="#"
        className="mn-has-sub opacity-1"
        onClick={(e) => { e.preventDefault(); toggle(); }}
      >
        {currentLocale.toUpperCase()} <i className="mi-chevron-down" />
      </a>
      <ul className={`mn-sub to-left ${isOpen ? 'open' : 'closed'}`}>
        {LOCALES.map((loc) => (
          <li key={loc}>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                changeLocale(loc);
              }}
            >
              {loc === 'es' ? "Español"
                : loc === 'en' ? "English"
                : loc === 'pt' ? "Português"
                : loc === 'fr' ? "Français"
                : loc === 'it' ? "Italiano"
                : loc === 'de' ? "Deutsch"
                : loc === 'ja' ? "日本語"
                : loc === 'tr' ? "Türkçe"
                : loc}
            </a>
          </li>
        ))}
      </ul>
    </li>
  );
}
